// Copyright (c) Docugami, Inc. All rights reserved.

import { startAppListening } from "store/listenerMiddleware";
import { frontendSlice } from "./frontendSlice";
import { AppDispatch, AppState } from "store";
import { appendDocuments } from "store/documents/actions";
import { DocumentState } from "store/documents/types";
import { ClusterDocument, FileInfo } from "networking/api";
import { ProviderId } from "networking/StorageProviders";

export interface DocsetDocument extends Omit<ClusterDocument, "createdAt"> {
    createdAt?: string;
}

export interface GetDocsetDocumentsRequest extends GetDocumentsRequestFilters {
    docsetId: string;
}

export enum SortableDocumentField {
    Filename = "filename"
}

export interface GetDocumentsRequestFilters {
    name?: string;
    limit?: number;
    cursor?: number;
    status?: string[];
    minPages?: number;
    maxPages?: number;
    minSize?: number;
    maxSize?: number;
    sortField?: SortableDocumentField;
    sortOrder?: "asc" | "desc";
}

export type GetDocumentsPageRequest = GetDocsetDocumentsRequest & { offset: number };

export interface GetDocumentsPageResponse {
    documents: DocsetDocument[];
    total: number;
}

export interface GetDirectoryFilesPageRequest {
    directoryId: string;
    offset?: number;
    limit?: number;
}

export interface GetDirectoryFilesPageResponse {
    files: FileInfo[];
    total: number;
}

export const documentsApis = frontendSlice.injectEndpoints({
    endpoints: (builder) => ({
        getDocumentsPage: builder.query<GetDocumentsPageResponse, GetDocumentsPageRequest>({
            query: (request) => ({
                url: `/clusters/${encodeURIComponent(request.docsetId)}/documents/paged`,
                method: 'GET',
                params: request
            }),
            providesTags: (result, _, request) => [
                { type: 'docset-documents', id: request.docsetId },
                ...(result?.documents ?? []).map(document => ({ type: 'docset-documents' as const, id: document.id }))
            ],
        }),
        getRootDirectoryId: builder.query<string, void>({
            query: () => ({
                url: '/documents/root',
                responseHandler: 'text'
            })
        }),
        getProviderRootDirectoryId: builder.query<string, ProviderId>({
            query: (providerId) => ({
                url: `/documents/provider-root/${providerId}`,
                responseHandler: 'text'
            }),
            providesTags: ['providerRootDirectory']
        }),
        getFilesByIds: builder.query<FileInfo[], string[]>({
            query: (ids) => ({
                url: '/documents/ids',
                method: 'POST',
                body: ids
            })
        }),
        getDirectoryFilesPage: builder.query<GetDirectoryFilesPageResponse, GetDirectoryFilesPageRequest>({
            query: (request) => ({
                url: `/documents/directory/${encodeURIComponent(request.directoryId)}/paged`,
                method: 'GET',
                params: { offset: request.offset, limit: request.limit }
            }),
            providesTags: (result, _, request) => [
                { type: 'directory-files', id: request.directoryId },
                ...(result?.files ?? []).map(file => ({ type: 'directory-files' as const, id: file.id }))
            ],
        })
    }),
});

// Selector to get the root directory ID from the query result
export const selectRootDirectoryId = (state: AppState) => documentsApis.endpoints.getRootDirectoryId.select()(state).data;

export const {
    useGetDocumentsPageQuery,
    useGetRootDirectoryIdQuery,
    useGetProviderRootDirectoryIdQuery,
    useLazyGetFilesByIdsQuery
} = documentsApis;

// Action to invalidate directory files cache after deletion
export const invalidateDirectoryFiles = (directoryId: string) =>
    frontendSlice.util.invalidateTags([{ type: 'directory-files', id: directoryId }]);

startAppListening({
    matcher: documentsApis.endpoints.getDocumentsPage.matchFulfilled,
    effect: (input, listenerApi) => {
        const originalTotal = documentsApis.endpoints.getDocumentsPage.select(input.meta.arg.originalArgs)(listenerApi.getOriginalState())?.data?.total;
        const currentTotal = input.payload.total;

        if (originalTotal !== undefined && currentTotal < originalTotal) {
            // Removal of documents can invalidate all pages so invalidate the documents cache for this docset
            listenerApi.dispatch(frontendSlice.util.invalidateTags([{ type: 'docset-documents', id: input.meta.arg.originalArgs.docsetId }]));
        }
        listenerApi.dispatch(updateDocumentsStateThunk(input.payload.documents));
    }
});

startAppListening({
    matcher: documentsApis.endpoints.getDirectoryFilesPage.matchFulfilled,
    effect: (input, listenerApi) => {
        const originalTotal = documentsApis.endpoints.getDirectoryFilesPage.select(input.meta.arg.originalArgs)(listenerApi.getOriginalState())?.data?.total;
        const currentTotal = input.payload.total;

        if (originalTotal !== undefined && currentTotal < originalTotal) {
            // Removal of files can invalidate all pages so invalidate the directory files cache
            listenerApi.dispatch(frontendSlice.util.invalidateTags([{ type: 'directory-files', id: input.meta.arg.originalArgs.directoryId }]));
        }
    }
});

function updateDocumentsStateThunk(documents: DocsetDocument[]) {
    return (dispatch: AppDispatch, getState: (() => AppState)) => {
        const allIds = getState().documents.allIds;
        const newIds: string[] = [];
        const indexedDocuments: { [id: string]: DocumentState } = {};

        for (const document of documents) {
            if (!allIds.includes(document.folderId)) {
                newIds.push(document.folderId);
                indexedDocuments[document.folderId] = { clusterDocument: { ...document, createdAt: undefined } };

            }
        }

        if (!!newIds.length) {
            dispatch(appendDocuments({ allIds: newIds, indexed: indexedDocuments }));
        }
    }
}